/**
 * useApiUsage — R3-S3 (F3.1)
 *
 * Laedt das Monatskontingent der Public API (Api-Key-Nutzung).
 * Endpoint: GET /api/v2/account/api_usage (Alias: /api/v2/account/api-usage)
 *
 * WICHTIG (Semantik): `quota_remaining` ist der im Backend gefuehrte
 * RESTZAEHLER (user.api_quota), `quota_used` der daraus abgeleitete Verbrauch
 * (limit - remaining). Die UI zeigt laut Runde-3-Entscheidung den VERBRAUCH
 * (used/limit) plus eine Zusatzzeile mit dem Rest.
 *
 * Plaene ohne API-Zugang bekommen KEIN 402, sondern 200 mit
 * api_access=false / quota_limit=0 — die Seite zeigt dann die Upsell-Karte.
 */
import { useQuery } from '@tanstack/react-query'
import apiClient from '../../lib/api-client'

export type ApiUsage = {
  api_key: string | null
  api_key_masked: string | null
  plan: string
  api_access: boolean
  quota_limit: number
  quota_remaining: number
  quota_used: number
  quota_used_pct: number
  period_start: string
  period_end: string
  resets_at: string
  rate_limit_per_hour: number
}

export const API_USAGE_QUERY_KEY = ['account', 'api-usage'] as const

export async function fetchApiUsage(): Promise<ApiUsage> {
  // Der Pfad ist (noch) nicht in @heliumtracker/api-types typisiert —
  // gleiche Vorgehensweise wie in useApiTokens.ts.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const client = apiClient as any
  const result = await client.GET('/api/v2/account/api_usage', {})
  if (result.error) throw result.error
  return result.data as ApiUsage
}

type UseApiUsageResult = {
  usage: ApiUsage | null
  isLoading: boolean
  isError: boolean
  refetch: () => void
}

export function useApiUsage(): UseApiUsageResult {
  const query = useQuery<ApiUsage>({
    queryKey: API_USAGE_QUERY_KEY,
    queryFn: fetchApiUsage,
    retry: false,
    staleTime: 60_000,
  })

  return {
    usage: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => query.refetch(),
  }
}
